import { useState, useEffect, useRef } from 'react';
import { api } from '../hooks/useAuth';

const SERVER_BASE = (process.env.REACT_APP_BASE_URL || api.defaults.baseURL || window.location.origin || '').replace(/\/$/, '');

const getMediaUrl = (url) => {
  if (!url) return null;

  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }

  if (url.startsWith('https://')) {
    return url;
  }

  return `${SERVER_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  .pt-overlay {
    position: fixed;
    inset: 0;
    background: rgba(8, 8, 12, 0.82);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999;
    padding: 16px;
    animation: pt-fade-in 0.18s ease;
  }

  @keyframes pt-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes pt-slide-up {
    from { opacity: 0; transform: translateY(20px) scale(0.98); }
    to { opacity: 1; transform: translateY(0) scale(1); }
  }

  @keyframes pt-spin {
    to { transform: rotate(360deg); }
  }

  @keyframes pt-shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }

  @keyframes pt-pop {
    0% { transform: scale(1); }
    50% { transform: scale(1.04); }
    100% { transform: scale(1); }
  }

  .pt-modal {
    background: #0f0f14;
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: 20px;
    width: 100%;
    max-width: 560px;
    max-height: 88vh;
    display: flex;
    flex-direction: column;
    font-family: 'DM Sans', sans-serif;
    animation: pt-slide-up 0.22s cubic-bezier(0.34, 1.56, 0.64, 1);
    box-shadow: 0 32px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.04);
    overflow: hidden;
  }

  .pt-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 22px 24px 18px;
    border-bottom: 1px solid rgba(255,255,255,0.06);
    flex-shrink: 0;
  }

  .pt-header-left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .pt-reel-thumb {
    width: 44px;
    height: 58px;
    border-radius: 8px;
    object-fit: cover;
    border: 1px solid rgba(255,255,255,0.1);
    flex-shrink: 0;
  }

  .pt-reel-thumb-placeholder {
    width: 44px;
    height: 58px;
    border-radius: 8px;
    background: linear-gradient(135deg, #1e1e2a, #2a2a3a);
    border: 1px solid rgba(255,255,255,0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
  }

  .pt-title {
    font-size: 16px;
    font-weight: 700;
    color: #f0f0f5;
    margin: 0 0 3px;
    letter-spacing: -0.2px;
  }

  .pt-subtitle {
    font-size: 12px;
    color: rgba(255,255,255,0.35);
    margin: 0;
    font-family: 'DM Mono', monospace;
    letter-spacing: 0.3px;
  }

  .pt-close {
    background: rgba(255,255,255,0.06);
    border: none;
    color: rgba(255,255,255,0.5);
    width: 32px;
    height: 32px;
    border-radius: 8px;
    cursor: pointer;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.15s, color 0.15s;
    flex-shrink: 0;
  }

  .pt-close:hover {
    background: rgba(255,255,255,0.1);
    color: #fff;
  }

  .pt-body {
    flex: 1;
    overflow-y: auto;
    padding: 20px 24px 24px;
    scrollbar-width: thin;
    scrollbar-color: rgba(255,255,255,0.1) transparent;
  }

  .pt-body::-webkit-scrollbar { width: 4px; }
  .pt-body::-webkit-scrollbar-track { background: transparent; }
  .pt-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }

  .pt-alert {
    padding: 10px 14px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 18px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pt-alert-success {
    background: rgba(52, 211, 153, 0.1);
    border: 1px solid rgba(52, 211, 153, 0.25);
    color: #34d399;
  }

  .pt-alert-error {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid rgba(239, 68, 68, 0.25);
    color: #f87171;
  }

  .pt-section-label {
    font-size: 10px;
    font-weight: 600;
    color: rgba(255,255,255,0.3);
    letter-spacing: 1.2px;
    text-transform: uppercase;
    margin: 0 0 12px;
  }

  .pt-section {
    margin-bottom: 28px;
  }

  .pt-tagged-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .pt-tagged-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    padding: 10px 12px;
    transition: border-color 0.15s;
  }

  .pt-tagged-card:hover {
    border-color: rgba(255,255,255,0.12);
  }

  .pt-product-img {
    width: 46px;
    height: 46px;
    border-radius: 8px;
    object-fit: cover;
    flex-shrink: 0;
    background: #1a1a24;
    border: 1px solid rgba(255,255,255,0.07);
  }

  .pt-product-img-placeholder {
    width: 46px;
    height: 46px;
    border-radius: 8px;
    background: linear-gradient(135deg, #1e1e2a, #2a2a38);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
  }

  .pt-product-info {
    flex: 1;
    min-width: 0;
  }

  .pt-product-name {
    font-size: 13px;
    font-weight: 600;
    color: #e8e8f0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    margin: 0 0 4px;
  }

  .pt-product-meta {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .pt-price {
    font-size: 12px;
    font-family: 'DM Mono', monospace;
    color: rgba(255,255,255,0.4);
  }

  .pt-cta-select {
    background: rgba(255,255,255,0.06);
    border: 1px solid rgba(255,255,255,0.1);
    color: rgba(255,255,255,0.7);
    font-size: 11px;
    font-family: 'DM Sans', sans-serif;
    font-weight: 500;
    border-radius: 6px;
    padding: 3px 6px;
    cursor: pointer;
    outline: none;
    transition: border-color 0.15s;
  }

  .pt-cta-select:hover {
    border-color: rgba(255,255,255,0.2);
  }

  .pt-remove-btn {
    background: none;
    border: none;
    color: rgba(255,255,255,0.2);
    cursor: pointer;
    font-size: 14px;
    padding: 4px;
    border-radius: 6px;
    transition: color 0.15s, background 0.15s;
    flex-shrink: 0;
    line-height: 1;
  }

  .pt-remove-btn:hover {
    color: #f87171;
    background: rgba(239, 68, 68, 0.1);
  }

  .pt-search-wrap {
    position: relative;
    margin-bottom: 12px;
  }

  .pt-search-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(255,255,255,0.25);
    font-size: 14px;
    pointer-events: none;
  }

  .pt-search-input {
    width: 100%;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.1);
    border-radius: 10px;
    padding: 11px 40px 11px 36px;
    font-size: 14px;
    font-family: 'DM Sans', sans-serif;
    color: #f0f0f5;
    outline: none;
    transition: border-color 0.15s, background 0.15s;
    box-sizing: border-box;
  }

  .pt-search-input::placeholder {
    color: rgba(255,255,255,0.2);
  }

  .pt-search-input:focus {
    border-color: rgba(139, 92, 246, 0.5);
    background: rgba(255,255,255,0.07);
  }

  .pt-spinner {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.1);
    border-top-color: rgba(139, 92, 246, 0.8);
    border-radius: 50%;
    animation: pt-spin 0.7s linear infinite;
  }

  .pt-results-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .pt-result-card {
    display: flex;
    align-items: center;
    gap: 12px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    border-radius: 12px;
    padding: 10px 12px;
    transition: border-color 0.15s, background 0.15s;
    animation: pt-slide-up 0.15s ease both;
  }

  .pt-result-card:hover {
    border-color: rgba(139, 92, 246, 0.3);
    background: rgba(139, 92, 246, 0.04);
  }

  .pt-result-actions {
    display: flex;
    gap: 6px;
    flex-shrink: 0;
  }

  .pt-tag-btn {
    border: none;
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 11px;
    font-weight: 600;
    font-family: 'DM Sans', sans-serif;
    cursor: pointer;
    transition: all 0.15s;
    white-space: nowrap;
    letter-spacing: 0.2px;
  }

  .pt-tag-btn-primary {
    background: rgba(139, 92, 246, 0.15);
    border: 1px solid rgba(139, 92, 246, 0.35);
    color: #a78bfa;
  }

  .pt-tag-btn-primary:hover {
    background: rgba(139, 92, 246, 0.25);
    border-color: rgba(139, 92, 246, 0.6);
    color: #c4b5fd;
    animation: pt-pop 0.2s ease;
  }

  .pt-tag-btn-secondary {
    background: rgba(52, 211, 153, 0.1);
    border: 1px solid rgba(52, 211, 153, 0.25);
    color: #6ee7b7;
  }

  .pt-tag-btn-secondary:hover {
    background: rgba(52, 211, 153, 0.2);
    border-color: rgba(52, 211, 153, 0.5);
    color: #34d399;
    animation: pt-pop 0.2s ease;
  }

  .pt-empty {
    text-align: center;
    padding: 28px 16px;
    color: rgba(255,255,255,0.2);
    font-size: 13px;
  }

  .pt-empty-icon {
    font-size: 28px;
    margin-bottom: 8px;
    opacity: 0.5;
  }

  .pt-skeleton {
    background: linear-gradient(90deg, rgba(255,255,255,0.04) 25%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 75%);
    background-size: 200% 100%;
    animation: pt-shimmer 1.4s infinite;
    border-radius: 8px;
  }

  .pt-count-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(139, 92, 246, 0.15);
    border: 1px solid rgba(139, 92, 246, 0.3);
    color: #a78bfa;
    font-size: 10px;
    font-weight: 700;
    font-family: 'DM Mono', monospace;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    margin-left: 6px;
    vertical-align: middle;
  }
`;

export default function ProductTaggerImproved({ reel, onClose }) {
  const [taggedProducts, setTaggedProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const messageTimerRef = useRef(null);

  const reelId = reel.video_id || reel.id;
  const reelPreviewUrl = getMediaUrl(reel.thumbnail_url) || getMediaUrl(reel.media_url);
  // ─── Helpers ──────────────────────────────────────────────────────────────
  const showMessage = (type, text) => {
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    setMessage({ type, text });
    messageTimerRef.current = setTimeout(() => setMessage(null), 3500);
  };

  // ─── Fetch tagged products on mount ───────────────────────────────────────
  useEffect(() => {
    fetchTaggedProducts();
    return () => {
      if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    };
  }, []);

  // ─── Debounced search ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => searchProducts(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchTaggedProducts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/reels/${reelId}/products`);
      setTaggedProducts(data.products || []);
    } catch (_) {
      showMessage('error', 'Failed to load tagged products');
    } finally {
      setLoading(false);
    }
  };

  // ─── Search: tries unified endpoint (Shopify → WooCommerce fallback) ──────
  const searchProducts = async (q) => {
    setSearching(true);
    try {
      // This endpoint handles Shopify first, then falls back to WooCommerce
      const { data } = await api.get('/api/shopify/products/search', { params: { q } });
      setSearchResults(data.products || []);
      if ((data.products || []).length === 0) {
        showMessage('error', 'No products found for that query');
      }
    } catch (e) {
      const msg = e.response?.data?.error || 'No store connected. Please connect Shopify or WooCommerce.';
      showMessage('error', msg);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const tagProduct = async (product, ctaType = 'add_to_cart') => {
    try {
      await api.post(`/api/reels/${reelId}/tag`, {
        product_id: product.id,
        product_title: product.title,
        product_image: product.image,
        product_price: product.price,
        cta_type: ctaType,
        position: 'bottom',
      });
      showMessage('success', `"${product.title}" tagged successfully!`);
      setSearchQuery('');
      setSearchResults([]);
      await fetchTaggedProducts();
    } catch (e) {
      showMessage('error', e.response?.data?.error || 'Failed to tag product');
    }
  };

  const removeTag = async (tagId) => {
    try {
      await api.delete(`/api/tags/${tagId}`);
      await fetchTaggedProducts();
      showMessage('success', 'Tag removed');
    } catch (_) {
      showMessage('error', 'Failed to remove tag');
    }
  };

  const updateTagCTA = async (tagId, newCtaType) => {
    try {
      await api.patch(`/api/tags/${tagId}`, { cta_type: newCtaType });
      await fetchTaggedProducts();
      showMessage('success', 'CTA updated');
    } catch (_) {
      showMessage('error', 'Failed to update CTA');
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const formatPrice = (price) => {
    if (!price) return null;
    const num = parseFloat(price);
    return isNaN(num) ? price : `₹${num.toLocaleString('en-IN')}`;
  };

  // ─── Skeleton loader ──────────────────────────────────────────────────────
  const SkeletonCard = () => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      background: 'rgba(255,255,255,0.02)',
      border: '1px solid rgba(255,255,255,0.05)',
      borderRadius: 12, padding: '10px 12px',
    }}>
      <div className="pt-skeleton" style={{ width: 46, height: 46, borderRadius: 8, flexShrink: 0 }} />
      <div style={{ flex: 1 }}>
        <div className="pt-skeleton" style={{ height: 12, width: '65%', marginBottom: 8 }} />
        <div className="pt-skeleton" style={{ height: 10, width: '35%' }} />
      </div>
    </div>
  );

  return (
    <>
      <style>{styles}</style>
      <div className="pt-overlay" onClick={handleOverlayClick}>
        <div className="pt-modal">

          {/* ── Header ── */}
          <div className="pt-header">
            <div className="pt-header-left">
              {reelPreviewUrl
                ? <img src={reelPreviewUrl} alt="" className="pt-reel-thumb" />
                : <div className="pt-reel-thumb-placeholder">🎬</div>
              }
              <div>
                <h3 className="pt-title">Tag Products</h3>
                <p className="pt-subtitle">
                  {reel.caption
                    ? reel.caption.slice(0, 38) + (reel.caption.length > 38 ? '…' : '')
                    : reelId}
                </p>
              </div>
            </div>
            <button className="pt-close" onClick={onClose} aria-label="Close">✕</button>
          </div>

          {/* ── Body ── */}
          <div className="pt-body">

            {/* Alert message */}
            {message && (
              <div className={`pt-alert pt-alert-${message.type}`}>
                <span>{message.type === 'success' ? '✓' : '⚠'}</span>
                {message.text}
              </div>
            )}

            {/* ── Tagged Products ── */}
            <div className="pt-section">
              <p className="pt-section-label">
                Tagged Products
                {taggedProducts.length > 0 && (
                  <span className="pt-count-badge">{taggedProducts.length}</span>
                )}
              </p>

              {loading ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <SkeletonCard />
                  <SkeletonCard />
                </div>
              ) : taggedProducts.length === 0 ? (
                <div className="pt-empty">
                  <div className="pt-empty-icon">🏷️</div>
                  No products tagged yet
                </div>
              ) : (
                <div className="pt-tagged-list">
                  {taggedProducts.map((p) => (
                    <div key={p.id} className="pt-tagged-card">
                      {p.product_image
                        ? <img src={p.product_image} alt={p.product_title} className="pt-product-img" />
                        : <div className="pt-product-img-placeholder">📦</div>
                      }
                      <div className="pt-product-info">
                        <p className="pt-product-name">{p.product_title || 'Unnamed product'}</p>
                        <div className="pt-product-meta">
                          {p.product_price && (
                            <span className="pt-price">{formatPrice(p.product_price)}</span>
                          )}
                          <select
                            className="pt-cta-select"
                            value={p.cta_type}
                            onChange={(e) => updateTagCTA(p.id, e.target.value)}
                          >
                            <option value="add_to_cart">Add to Cart</option>
                            <option value="buy_now">Buy Now</option>
                          </select>
                        </div>
                      </div>
                      <button
                        className="pt-remove-btn"
                        onClick={() => removeTag(p.id)}
                        aria-label="Remove tag"
                        title="Remove tag"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Search ── */}
            <div className="pt-section">
              <p className="pt-section-label">Add Product</p>
              <div className="pt-search-wrap">
                <span className="pt-search-icon">🔍</span>
                <input
                  className="pt-search-input"
                  placeholder="Search your catalog…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoComplete="off"
                />
                {searching && <div className="pt-spinner" />}
              </div>

              {/* Search results */}
              {searchResults.length > 0 && (
                <div className="pt-results-list">
                  {searchResults.map((p, i) => (
                    <div
                      key={p.id}
                      className="pt-result-card"
                      style={{ animationDelay: `${i * 40}ms` }}
                    >
                      {p.image
                        ? <img src={p.image} alt={p.title} className="pt-product-img" />
                        : <div className="pt-product-img-placeholder">📦</div>
                      }
                      <div className="pt-product-info">
                        <p className="pt-product-name">{p.title}</p>
                        {p.price && (
                          <span className="pt-price">{formatPrice(p.price)}</span>
                        )}
                      </div>
                      <div className="pt-result-actions">
                        <button
                          className="pt-tag-btn pt-tag-btn-primary"
                          onClick={() => tagProduct(p, 'add_to_cart')}
                          title="Tag with Add to Cart"
                        >
                          🛒 Cart
                        </button>
                        <button
                          className="pt-tag-btn pt-tag-btn-secondary"
                          onClick={() => tagProduct(p, 'buy_now')}
                          title="Tag with Buy Now"
                        >
                          ⚡ Buy
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Empty states */}
              {searchQuery && !searching && searchResults.length === 0 && (
                <div className="pt-empty">
                  <div className="pt-empty-icon">🔍</div>
                  No products match "{searchQuery}"
                </div>
              )}

              {!searchQuery && (
                <div className="pt-empty" style={{ paddingTop: 16 }}>
                  Type to search your Shopify or WooCommerce catalog
                </div>
              )}
            </div>

          </div>
        </div>
      </div>
    </>
  );
}
